import React, { useState } from 'react';
import { Menu, ChevronDown } from 'lucide-react';
import type { HeadingItem } from './blogPostUtils.js';

interface BlogTableOfContentsProps {
  headings: HeadingItem[];
  activeId: string;
  onHeadingClick: (e: React.MouseEvent<HTMLAnchorElement>, id: string) => void;
  variant: 'desktop' | 'mobile';
}

export const BlogTableOfContents: React.FC<BlogTableOfContentsProps> = ({
  headings,
  activeId,
  onHeadingClick,
  variant,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (variant === 'desktop') {
    return (
      <aside className="hidden lg:block lg:col-span-3 sticky top-28 self-start max-h-[calc(100vh-8rem)] overflow-y-auto pr-6 border-r border-slate-100">
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-slate-800 font-extrabold text-xs uppercase tracking-widest">
            <Menu className="w-4 h-4 text-indigo-600" />
            <span>Daftar Isi</span>
          </div>
          {headings.length > 0 ? (
            <nav className="space-y-1 mt-2">
              {headings.map((heading, idx) => (
                <a
                  key={`toc-desk-${heading.id}-${idx}`}
                  href={`#${heading.id}`}
                  onClick={(e) => onHeadingClick(e, heading.id)}
                  className={`block text-xs font-bold transition-all duration-200 border-l-2 py-2 pl-3 leading-relaxed cursor-pointer ${
                    activeId === heading.id
                      ? 'border-indigo-600 text-indigo-600 font-extrabold translate-x-1'
                      : 'border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-200'
                  } ${heading.level === 3 ? 'ml-3 text-[11px]' : ''}`}
                >
                  {heading.text}
                </a>
              ))}
            </nav>
          ) : (
            <p className="text-[11px] text-slate-400 italic">Membaca artikel...</p>
          )}
        </div>
      </aside>
    );
  }

  // Mobile / Tablet Variant
  if (headings.length === 0) return null;

  return (
    <div className="lg:hidden mb-10 p-5 rounded-3xl bg-slate-50 border border-slate-100 transition-all duration-300">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between cursor-pointer select-none outline-none"
      >
        <div className="flex items-center gap-2 text-slate-800 font-extrabold text-xs uppercase tracking-widest">
          <Menu className="w-4 h-4 text-indigo-600" />
          <span>Daftar Isi Artikel</span>
        </div>
        <span className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDown className="w-4 h-4 text-slate-500" />
        </span>
      </button>
      
      {/* Smooth collapsible height and opacity grid transition */}
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isOpen ? 'grid-rows-[1fr] opacity-100 mt-4 pt-4 border-t border-slate-200/50' : 'grid-rows-[0fr] opacity-0 pointer-events-none'
        }`}
      >
        <div className="overflow-hidden">
          <nav className="space-y-1.5 text-left">
            {headings.map((heading, idx) => (
              <a
                key={`toc-mob-${heading.id}-${idx}`}
                href={`#${heading.id}`}
                onClick={(e) => {
                  onHeadingClick(e, heading.id);
                  setIsOpen(false);
                }}
                className={`block text-xs font-bold transition-colors py-1 cursor-pointer ${
                  activeId === heading.id
                    ? 'text-indigo-600 font-extrabold'
                    : 'text-slate-500 hover:text-indigo-600'
                } ${heading.level === 3 ? 'pl-4 text-[11px]' : ''}`}
              >
                {heading.text}
              </a>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
};
